import React, { useRef, useEffect, useState } from 'react';
import { useThemeContext } from '../context/ThemeProvider';
import { useLanguage } from '../context/LanguageProvider';
import { TIME_SIGNATURES } from '../config/timeSignatures';
import ActionButton from './common/ActionButton';
import { DrumGridProps, HitLevel } from '../types';

const HIT_LEVELS: HitLevel[] = [null, 'x', 'X', 'g', 'f'];

export default function DrumGrid({
  instruments,
  grid,
  setGrid,
  stepsPerBar,
  setStepsPerBar,
  timeSignature,
  noteDivision,
  isPlaying,
  isPaused,
  step,
  advancedMode,
  HIT_LABELS,
  t,
  exportPattern,
  triggerImport,
  resetPattern,
  onGridModified
}: DrumGridProps) {
  const { getClass } = useThemeContext();
  const getThemeClass = getClass;
  const { t: translate } = useLanguage();
  const _t = t || translate;

  // 追焦功能相關狀態和refs
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const [isFollowing, setIsFollowing] = useState<boolean>(true);
  const [showFollowHint, setShowFollowHint] = useState<boolean>(false);

  // 當播放時自動追焦到當前步數
  useEffect(() => {
    if ((isPlaying || isPaused) && isFollowing && gridContainerRef.current) {
      const currentStepButton = gridContainerRef.current.querySelector(`[data-step="${step}"]`);
      if (currentStepButton) {
        currentStepButton.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center'
        });
      }
      
      // 顯示追焦提示
      if (isPlaying && !showFollowHint) {
        setShowFollowHint(true);
      }
    }
  }, [step, isPlaying, isPaused, isFollowing, showFollowHint]);

  // 當停止播放時隱藏提示
  useEffect(() => {
    if (!isPlaying && !isPaused) {
      setShowFollowHint(false);
      setIsFollowing(true); // 重置追焦狀態
    }
  }, [isPlaying, isPaused]);

  // 停止追焦的處理函數
  const handleStopFollowing = (): void => {
    setIsFollowing(false);
    setShowFollowHint(false);
  };

  const cycleHit = (inst: string, idx: number): void => {
    setGrid(prev => {
      if (!prev[inst]) return prev;
      const current = prev[inst][idx] as HitLevel;
      let next: HitLevel;
      if (advancedMode) {
        next = HIT_LEVELS[(HIT_LEVELS.indexOf(current) + 1) % HIT_LEVELS.length];
      } else {
        next = current ? null : 'x';
      }
      const updated = [...prev[inst]];
      updated[idx] = next;
      
      // 通知grid被修改
      if (onGridModified) {
        onGridModified();
      }
      
      return { ...prev, [inst]: updated };
    });
  };

  return (
    <div className={`space-y-2 p-3 rounded ${getThemeClass('grid')} ${getThemeClass('border')}`}>
      {/* 追焦提示訊息 */}
      {showFollowHint && isFollowing && (
        <div className={`flex items-center justify-between p-2 rounded border-l-4 border-blue-400 bg-blue-50 text-blue-800 text-sm`}>
          <div className="flex items-center space-x-2">
            <span className="text-lg">👁️</span>
            <span>{_t('followingPlayback')}</span>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-xs">{_t('pauseToStopFollowing')}</span>
            <button
              onClick={handleStopFollowing}
              className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs"
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Drum Grid標題區域 - 包含標題和按鈕 */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <h2 className="text-xl font-bold">🥁 {_t('drumGrid')}</h2>
        
        {/* 按鈕區域 - 統一樣式 */}
        <div className="flex space-x-1">
          <ActionButton
            onClick={triggerImport}
            icon="📥"
            text={_t('importPattern')}
            tooltip={_t('importPattern')}
          />

          <ActionButton
            onClick={exportPattern}
            icon="📤"
            text={_t('exportPattern')}
            tooltip={_t('exportPattern')}
          />
          
          <ActionButton
            onClick={resetPattern}
            icon="🗑️"
            text={_t('resetPattern')}
            tooltip={_t('resetPatternTooltip') || 'Clear all drum patterns'}
            variant="danger"
          />
        </div>
      </div>

      {/* 步數標題 - 根據time signature調整重音顯示 */}
      <div 
        ref={gridContainerRef}
        className="overflow-x-auto"
      >
        <div className="flex items-center space-x-1 ml-12 pb-1">
          {Array(stepsPerBar).fill(0).map((_, idx) => {
            let isAccentBeat = false;
            const { beats } = TIME_SIGNATURES[timeSignature];
            const stepsPerBeat = stepsPerBar / beats;
            if (idx % stepsPerBeat === 0) {
              isAccentBeat = true;
            }
            return (
              <div
                key={idx}
                data-step={idx}
                className={`w-6 h-6 flex items-center justify-center text-xs font-mono shrink-0 ${
                  isAccentBeat ? 'text-blue-600 font-bold bg-blue-100 rounded' : 'text-gray-500'
                }`}
              >
                {idx + 1}
              </div>
            );
          })}
        </div>

        {/* 樂器行 */}
        <div className="flex flex-col space-y-2">
          {instruments.map(({ id, color }) => (
            <div key={id} className="flex items-center space-x-1">
              <span className="w-10 font-mono text-sm font-bold text-center shrink-0">{id}</span>
              <div className="flex space-x-1">
                {Array(stepsPerBar).fill(0).map((_, idx) => {
                  const hit = grid[id] ? grid[id][idx] : null;
                  const isAccent = hit === 'X';
                  return (
                    <button
                      key={idx}
                      data-step={idx}
                      onClick={() => cycleHit(id, idx)}
                      className={`w-6 h-6 rounded text-sm font-bold border ${getThemeClass('border')} transition-colors duration-150 shrink-0 ${
                        hit ? `${color} ${isAccent ? 'bg-opacity-100' : 'bg-opacity-70'} text-white` : `${getThemeClass('background')} ${getThemeClass('text')}`
                      } ${step === idx && (isPlaying || isPaused) ? `ring-2 ${getThemeClass('highlight')}` : ''}`}
                      title={hit ? HIT_LABELS[hit] || 'Custom' : _t('hitTypes.rest')}
                    >
                      {hit || '-'}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
} 